import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import betslip from '@gin-betting/betslip';
import core, { API, App, GQL } from '@gin-betting/core';
import restrictions, { Restrictions } from '@gin-betting/restrictions';
import { difference, includes, isEmpty } from 'lodash';
import createCachedSelector from 're-reselect';
import { createSelector, createStructuredSelector } from 'reselect';
import type { Arg1 } from 'tsargs';

import {
    Destination,
    ENABLE_PLACE_BET_LOCALSTORAGE_KEY,
    EventType,
    SELECTOR_STUB,
} from 'app-constants';
import { Modules, useAsyncModule } from 'async-modules';
import { useEventHandler, usePollingMinStake } from 'hooks';
import features from 'services/feature-manager';
import type { State } from 'types';
import { NOT_ENOUGH_BALANCE_REASON_LIST } from '../constants';
import PlaceBetContext from '../PlaceBetContext';
import { RestrictionsContext } from '../restrictions';
import { makeSelectIsFreebet } from '../selectors';
import { getPlaceBetMeta, getRestrictionBlackList } from '../utils';

type RestrictionsModule = typeof restrictions;

const {
    entities: {
        selectors: { selectBets },
    },
    utils: { getBettingOptions },
} = core;
const {
    constants: { BETSLIP_ODD_STATUSES_TO_PLACE_BET },
    actions: { placeBet },
    selectors: {
        selectConflictBetslipOddIds,
        selectBetslipBet,
        selectBetslipOddsByChosenBetslipBet,
        selectBetslipBetIdFromProps,
        selectChosenBetslipBet,
        selectBetslipBetWithStatus,
        selectActiveBetslipOddIdsByBetslipBet,
    },
} = betslip;

interface Input {
    betslipBetId: string;
    totalStake: number;
}

const { isAuthorized } = getBettingOptions();

const selectMinStake = (_state: State, props: { minStake: number }): number =>
    props.minStake;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const makeSelectIsAnyOddHaveError = ({
    selectors: { selectRestrictionsByBetslipBet },
    utils: { normalizeRestriction },
}: RestrictionsModule) =>
    createCachedSelector(
        selectRestrictionsByBetslipBet,
        selectBetslipBetWithStatus,
        (restrictionsBet, betslipBet): boolean => {
            if (!betslipBet) return false;

            const { simpleRestrictions } = normalizeRestriction(
                restrictionsBet
            );

            const restrictionTypes = simpleRestrictions.map(r => r.type);
            const blacklist = getRestrictionBlackList(betslipBet.status);

            return difference(restrictionTypes, blacklist).length > 0;
        }
    )(selectBetslipBetIdFromProps);

const selectUnaffectedBetslipOddIds = createCachedSelector(
    selectChosenBetslipBet,
    selectBetslipOddsByChosenBetslipBet,
    selectActiveBetslipOddIdsByBetslipBet,
    selectMinStake,
    (betslipBet, betslipOdds, activeBetslipOddIds, minBet): string[] => {
        if (betslipBet?.mode !== GQL.BetType.SINGLE) {
            return [];
        }

        const unaffectedBetslipOddIds = betslipOdds.filter(
            ({ singleStake, singleStatus, id }) => {
                return (
                    !activeBetslipOddIds.includes(id) ||
                    +singleStake < minBet ||
                    !includes(BETSLIP_ODD_STATUSES_TO_PLACE_BET, singleStatus)
                );
            }
        );

        return unaffectedBetslipOddIds.keySeq().toArray();
    }
)(selectBetslipBetIdFromProps);

const selectIsAnyOddHaveNotEnoughBalance = createCachedSelector(
    selectBetslipOddsByChosenBetslipBet,
    selectBets,
    (betslipOdds, bets): boolean =>
        betslipOdds.some(({ betId }) => {
            if (!betId) return false;

            const bet = bets.get(betId);

            if (!bet) return false;

            return includes(NOT_ENOUGH_BALANCE_REASON_LIST, bet.declineReason);
        })
)(selectBetslipBetIdFromProps);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const makeSelectUnaffectedBetslipOddIds = ({
    selectors: { selectBetslipRestrictions },
    utils: { normalizeRestriction, getBetslipRestrictionId },
}: RestrictionsModule) =>
    createCachedSelector(
        selectBetslipBetWithStatus,
        selectBetslipOddsByChosenBetslipBet,
        selectBetslipRestrictions,
        selectUnaffectedBetslipOddIds,
        (
            betslipBet,
            betslipOdds,
            betslipRestrictions,
            unaffectedBetslipOddIds
        ): string[] => {
            if (betslipBet?.mode !== GQL.BetType.SINGLE) {
                return [];
            }

            const extendedUnaffectedBetslipOdds = betslipOdds.filter(
                ({ singleStake, id: betslipOddId }) => {
                    if (unaffectedBetslipOddIds.includes(betslipOddId)) {
                        return true;
                    }

                    const betslipRestrictionsId = getBetslipRestrictionId(
                        betslipOddId
                    );

                    const betslipOddRestriction = betslipRestrictions.get(
                        betslipRestrictionsId
                    );

                    if (!betslipOddRestriction) return false;

                    const {
                        maxBetRestriction,
                        simpleRestrictions,
                    } = normalizeRestriction(betslipOddRestriction);

                    const blackList = getRestrictionBlackList(
                        betslipBet.status
                    );

                    const restrictionErrors = simpleRestrictions.filter(
                        ({ type }) =>
                            !includes(
                                [
                                    ...blackList,
                                    API.RestrictionType.selection_value,
                                ],
                                type
                            )
                    );

                    if (restrictionErrors.length > 0) {
                        return true;
                    }

                    return (
                        maxBetRestriction &&
                        +singleStake > maxBetRestriction.maxBet
                    );
                }
            );

            return extendedUnaffectedBetslipOdds.keySeq().toArray();
        }
    )(selectBetslipBetIdFromProps);

interface Output {
    placeBetHandler: VoidFn;
    isDisabled: boolean;
    isAuthorized: boolean;
    isNeedDeposit: boolean;
    authHandler: VoidFn;
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const makeSelectIsMinMaxBetError = (restrictionsModule: RestrictionsModule) => {
    const {
        selectors: { selectBetslipRestrictions },
    } = restrictionsModule;

    const getRestrictionsAndCheckMaxStake = makeGetRestrictionsAndCheckMaxStake(
        restrictionsModule
    );

    return createSelector(
        selectBetslipBet,
        selectBetslipOddsByChosenBetslipBet,
        selectBetslipRestrictions,
        selectMinStake,
        (betslipBet, betslipOdds, betslipRestrictions, minStake): boolean => {
            if (!betslipBet) return false;

            const { mode, stake, betslipOddIds } = betslipBet;

            if ([GQL.BetType.SYSTEM, GQL.BetType.EXPRESS].includes(mode)) {
                return (
                    getRestrictionsAndCheckMaxStake(
                        betslipRestrictions,
                        betslipOddIds,
                        stake
                    ) ||
                    (!isEmpty(stake) && +stake < minStake)
                );
            }

            if (mode === GQL.BetType.SINGLE) {
                return betslipOdds.every(
                    ({ singleStake, id }) =>
                        getRestrictionsAndCheckMaxStake(
                            betslipRestrictions,
                            id,
                            singleStake
                        ) ||
                        (!isEmpty(singleStake) && +singleStake < minStake)
                );
            }

            return false;
        }
    );
};

const makeSelectProps = ({
    restrictionsModule,
    freebetModule,
}: {
    restrictionsModule: RestrictionsModule | null;
    freebetModule: Modules['freebet']['default'] | null;
}) => {
    const selectIsMinMaxBetError = restrictionsModule
        ? makeSelectIsMinMaxBetError(restrictionsModule)
        : SELECTOR_STUB;
    const selectIsAnyOddHaveError = restrictionsModule
        ? makeSelectIsAnyOddHaveError(restrictionsModule)
        : SELECTOR_STUB;

    const selectExtendedUnaffectedBetslipOddIds = restrictionsModule
        ? makeSelectUnaffectedBetslipOddIds(restrictionsModule)
        : selectUnaffectedBetslipOddIds;

    return createStructuredSelector({
        betslipBet: selectBetslipBetWithStatus,
        conflictBetslipOddIds: selectConflictBetslipOddIds,
        unaffectedBetslipOddIds: selectExtendedUnaffectedBetslipOddIds,
        isMinMaxBetError: selectIsMinMaxBetError,
        isAnyOddHaveError: selectIsAnyOddHaveError,
        isAnyOddHaveNotEnoughBalance: selectIsAnyOddHaveNotEnoughBalance,
        activeBetslipOddIdsByBetslipBet: selectActiveBetslipOddIdsByBetslipBet,
        isFreebetActive: makeSelectIsFreebet(freebetModule),
    });
};

const usePlaceBet = ({ totalStake, betslipBetId }: Input): Output => {
    const dispatch = useDispatch();
    const placeBetCallback = useContext(PlaceBetContext);
    const { state: restrictionsState } = useContext(RestrictionsContext);
    const { minStake } = usePollingMinStake();
    const freebetModule = useAsyncModule('freebet');

    const selectProps = makeSelectProps({
        restrictionsModule: restrictions,
        freebetModule,
    });

    const {
        dispatchBettingEvent: dispatchRedirectBettingEvent,
        isEventAvailable: isRedirectEventAvailable,
    } = useEventHandler({
        eventType: EventType.Redirect,
    });

    const {
        dispatchBettingEvent: dispatchNotEnoughBalanceBettingEvent,
        isEventAvailable: isNotEnoughBalanceEventAvailable,
    } = useEventHandler({
        eventType: EventType.HandleNotEnoughBalance,
    });

    const {
        betslipBet,
        conflictBetslipOddIds,
        unaffectedBetslipOddIds,
        isMinMaxBetError,
        isAnyOddHaveError,
        isAnyOddHaveNotEnoughBalance,
        activeBetslipOddIdsByBetslipBet,
        isFreebetActive,
    } = useSelector((state: State) =>
        selectProps(state, { betslipBetId, restrictionsState, minStake })
    );

    const checkIsDisabled = (): boolean => {
        if (
            localStorage.getItem(ENABLE_PLACE_BET_LOCALSTORAGE_KEY) ||
            isFreebetActive
        ) {
            return false;
        }

        if (!isAuthorized || !betslipBet) return false;

        const { betslipOddIds, mode, systemSize, status } = betslipBet;

        if (
            [GQL.BetType.SYSTEM, GQL.BetType.EXPRESS].includes(mode) &&
            (status === App.BetslipStatus.SUCCESS ||
                conflictBetslipOddIds.length > 0 ||
                isAnyOddHaveError ||
                !betslipOddIds.equals(activeBetslipOddIdsByBetslipBet))
        ) {
            return true;
        }

        const isAllBetslipOddsUnaffected =
            betslipOddIds.size === unaffectedBetslipOddIds.length;

        if (
            betslipOddIds.size === 0 ||
            isMinMaxBetError ||
            totalStake <= 0 ||
            isAllBetslipOddsUnaffected
        ) {
            return true;
        }

        if (mode === GQL.BetType.SYSTEM) {
            return systemSize.length === 0;
        }

        return false;
    };

    const isDisabled = checkIsDisabled();

    const isBetNotEditable =
        !!betslipBet &&
        !includes(BETSLIP_ODD_STATUSES_TO_PLACE_BET, betslipBet.status);

    const isRefillModal = features.isActive(
        features.list.BetslipRefillBalanceModal
    );

    const isNeedDeposit =
        isAnyOddHaveNotEnoughBalance && isBetNotEditable && isDisabled;

    const placeBetHandler = useCallback(() => {
        if (isNeedDeposit && !isRefillModal) {
            if (isNotEnoughBalanceEventAvailable) {
                dispatchNotEnoughBalanceBettingEvent({});
            } else {
                // fallback
                window.location.href = '#!/player/profile-deposit';
            }

            return;
        }

        if (isDisabled) return;

        dispatch(
            placeBet({
                unaffectedBetslipOddIds:
                    localStorage.getItem(ENABLE_PLACE_BET_LOCALSTORAGE_KEY) ||
                    isFreebetActive
                        ? []
                        : unaffectedBetslipOddIds,
                stakeMode: GQL.StakeMode.total,
                meta: getPlaceBetMeta(),
            })
        );
        placeBetCallback();
    }, [
        isDisabled,
        dispatch,
        isFreebetActive,
        unaffectedBetslipOddIds,
        placeBetCallback,
    ]);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                placeBetHandler();
            }
        },
        [placeBetHandler]
    );

    const authHandler = useCallback(() => {
        if (isRedirectEventAvailable) {
            dispatchRedirectBettingEvent({
                destination: Destination.Login,
                link: '',
            });
        } else {
            // fallback
            window.location.href = '#!/auth/signin?popup';
        }
    }, [isRedirectEventAvailable, dispatchRedirectBettingEvent]);

    return {
        placeBetHandler,
        isDisabled,
        isAuthorized,
        isNeedDeposit,
        authHandler,
        onKeyDown,
    };
};

export default usePlaceBet;

function makeGetRestrictionsAndCheckMaxStake({
    utils: { getBetslipRestrictionId, checkIsMaxBetError },
}: RestrictionsModule) {
    return (
        betslipRestrictions: Restrictions.RestrictionsState['betslipRestrictions'],
        betslipOddIds: Arg1<typeof getBetslipRestrictionId>,
        stake: string
    ) => {
        const betslipRestrictionId = getBetslipRestrictionId(betslipOddIds);
        const betslipRestriction = betslipRestrictions.get(
            betslipRestrictionId
        );

        if (!betslipRestriction) return false;

        return checkIsMaxBetError(betslipRestriction, stake);
    };
}
